import type { CellTraceData, TraceItem } from "./types";

/**
 * Fetch the active cell's address/formula/value plus its direct precedents
 * and direct dependents.
 *
 * Each side (precedents, dependents) runs in its own `Excel.run` so a
 * failure on one side doesn't kill the other. If, for example, a cell has
 * no precedents, `getDirectPrecedents()` may throw during sync — that
 * shouldn't prevent us from still reporting dependents.
 */
export async function getTraceForActiveCell(): Promise<CellTraceData> {
  const active = await loadActiveCell();
  const precedents = await loadDirect("precedents");
  const dependents = await loadDirect("dependents");

  return {
    active,
    precedents: precedents.items,
    dependents: dependents.items,
    precedentsError: precedents.error,
    dependentsError: dependents.error,
  };
}

async function loadActiveCell(): Promise<CellTraceData["active"]> {
  return Excel.run(async (ctx) => {
    const cell = ctx.workbook.getActiveCell();
    cell.load(["address", "formulas", "values"]);
    // eslint-disable-next-line office-addins/load-object-before-read, office-addins/call-sync-before-read
    const ws = cell.worksheet;
    ws.load("name");
    await ctx.sync();

    /* eslint-disable office-addins/call-sync-after-load, office-addins/call-sync-before-read */
    const fullAddress = cell.address;
    const sheetName = ws.name;
    const formulaCell = cell.formulas[0];
    const valueCell = cell.values[0];
    /* eslint-enable office-addins/call-sync-after-load, office-addins/call-sync-before-read */

    const formula = typeof formulaCell?.[0] === "string" ? (formulaCell[0] as string) : "";
    const valueRaw = valueCell?.[0];
    return {
      fullAddress,
      sheetName,
      localAddress: stripSheet(fullAddress),
      formula,
      value: valueRaw === undefined ? "" : String(valueRaw),
    };
  });
}

async function loadDirect(
  kind: "precedents" | "dependents"
): Promise<{ items: TraceItem[]; error?: string }> {
  try {
    return {
      items: await Excel.run(async (ctx) => {
        const cell = ctx.workbook.getActiveCell();
        const areas =
          kind === "precedents" ? cell.getDirectPrecedents() : cell.getDirectDependents();
        areas.load("addresses");
        await ctx.sync();
        return expandAddresses(areas.addresses);
      }),
    };
  } catch (err) {
    return { items: [], error: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * `WorkbookRangeAreas.addresses` returns one string per worksheet that has
 * ranges in the areas; when that worksheet has multiple discontiguous
 * ranges they're comma-joined inside a single entry (each part still
 * carrying its own sheet qualifier). Expand them into one TraceItem per
 * rectangular range so each is independently clickable in the UI.
 */
function expandAddresses(addresses: string[]): TraceItem[] {
  const out: TraceItem[] = [];
  // Match one address at a time: sheet (quoted-with-comma-OK, or
  // unquoted-no-comma) + "!" + range (no comma).
  const RE = /('(?:[^']|'')+'|[^!,]+)!([^,]+)/g;
  for (const entry of addresses) {
    RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = RE.exec(entry)) !== null) {
      let sheet = m[1].trim();
      if (sheet.startsWith("'") && sheet.endsWith("'")) {
        sheet = sheet.slice(1, -1).replace(/''/g, "'");
      }
      const local = m[2].trim();
      out.push({
        fullAddress: `${sheet}!${local}`,
        sheetName: sheet,
        localAddress: local,
      });
    }
  }
  return out;
}

function stripSheet(full: string): string {
  const m = full.match(/!(.+)$/);
  return m ? m[1] : full;
}
